/**
 * CurrencyFilter Component
 * TASK-207: Portfolio Level View - Enhanced Filters
 *
 * Multi-select button group for currency filtering
 * Allows selection of multiple currencies to filter portfolio positions
 */

import React from 'react';
import { CurrencyFilter as CurrencyFilterType } from '../../../types/portfolio-views';

interface CurrencyFilterProps {
  selected: CurrencyFilterType[];
  onChange: (selected: CurrencyFilterType[]) => void;
  disabled?: boolean;
}

const CurrencyFilter: React.FC<CurrencyFilterProps> = ({
  selected,
  onChange,
  disabled = false,
}) => {
  const currencies: { value: CurrencyFilterType; label: string; symbol: string }[] = [
    { value: 'all', label: 'All', symbol: '' },
    { value: 'CHF', label: 'CHF', symbol: 'CHF' },
    { value: 'USD', label: 'USD', symbol: '$' },
    { value: 'EUR', label: 'EUR', symbol: '€' },
    { value: 'GBP', label: 'GBP', symbol: '£' },
    { value: 'JPY', label: 'JPY', symbol: '¥' },
  ];

  const handleToggle = (currency: CurrencyFilterType) => {
    if (disabled) return;

    if (currency === 'all') {
      // Clicking "All" resets to all selected
      onChange(['all']);
    } else {
      // Remove 'all' from current selection
      const current = selected.filter((s) => s !== 'all');
      const isSelected = current.includes(currency);

      if (isSelected) {
        // Deselect this currency
        const newSelected = current.filter((s) => s !== currency);
        // If nothing selected, default to 'all'
        onChange(newSelected.length > 0 ? newSelected : ['all']);
      } else {
        // Add this currency to selection
        onChange([...current, currency]);
      }
    }
  };

  const isSelected = (currency: CurrencyFilterType): boolean => {
    if (selected.includes('all')) {
      return currency === 'all';
    }
    return selected.includes(currency);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Currency
      </label>
      <div className="flex flex-wrap gap-2">
        {currencies.map((currency) => {
          const isActive = isSelected(currency.value);

          return (
            <button
              key={currency.value}
              type="button"
              onClick={() => handleToggle(currency.value)}
              disabled={disabled}
              className={`
                px-3 py-1.5 rounded-md text-sm font-medium
                border transition-all duration-200
                ${
                  isActive
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {currency.symbol && currency.value !== 'all' && (
                <span className="mr-1">{currency.symbol}</span>
              )}
              {currency.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-gray-500">
        {selected.includes('all')
          ? 'Showing all currencies'
          : `Showing ${selected.length} ${selected.length === 1 ? 'currency' : 'currencies'}`}
      </p>
    </div>
  );
};

export default CurrencyFilter;
